import { useCallback, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuthStore } from "../auth/auth-store.js";
import { getRuntimeConfig } from "../config/runtime.js";
import {
  getPlayerAiChatRecovery,
  playerAiChatRecoveryQueryKey,
} from "../api/tenant-settings-api.js";

/* The tenant's "Backup connection for Player AI" setting (d33, d35).

   "Tenant wins": the effective value is the tenant's setting when a Tenant
   Admin has set it, otherwise the deployment's `/config.js`
   `playerAi.pollFallback` default. */

/**
 * THE ONLY definition of this query: the Player AI client's getter and the
 * admin control both read it, so they share one fetch and one set of options.
 *
 * Not before sign-in — the provider that calls it sits above the public routes
 * — and refetched on focus (the root client turns that off), so a long-open tab
 * picks up an administrator's change once the 5 minutes are up.
 */
export function usePlayerAiChatRecovery() {
  const signedIn = useAuthStore((state) => state.status === "authenticated");
  const tenantId = useAuthStore((state) => state.claims?.tenantId ?? null);
  return useQuery({
    queryKey: playerAiChatRecoveryQueryKey(tenantId),
    queryFn: getPlayerAiChatRecovery,
    enabled: signedIn && tenantId !== null,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: true,
  });
}

/** The tenant's value when set; the deployment default when null or unknown. */
export function effectivePollFallback(
  tenantValue: boolean | null | undefined,
): boolean {
  return tenantValue ?? getRuntimeConfig().playerAi.pollFallback;
}

/**
 * A stable getter for `PlayerAIClientOptions.pollFallback`. The client asks it
 * at each fallback decision, so the value read here may arrive after the live
 * line opened; reading it through a ref keeps the getter's identity, and with
 * it the client, unchanged. A read that is loading or failed has no data, and
 * the deployment default applies.
 */
export function usePlayerAiPollFallback(): () => boolean {
  const tenantValue = usePlayerAiChatRecovery().data?.enabled ?? null;
  const latest = useRef(tenantValue);
  useEffect(() => {
    latest.current = tenantValue;
  }, [tenantValue]);
  return useCallback(() => effectivePollFallback(latest.current), []);
}
